if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
      class ProductInfo extends HTMLElement {
      // Keeps thumbnail-driven variant sync working even when Shopify omits the embedded variants JSON.
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

	        this.initQuantityHandlers();
	        this.initSelectedVariantMediaHandler();
	        this.initThumbnailVariantSync();
	        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
	      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const currentSelectedOptions = this.getCurrentSelectedOptions();
        const resolvedVariant = this.findVariantByOptions(currentSelectedOptions);
        if (resolvedVariant) {
          this.syncLocalVariantState(resolvedVariant);
        }
        const variantForMedia = resolvedVariant || this.findVariantByOptions(
          currentSelectedOptions.length ? currentSelectedOptions : selectedOptionValues
        );
        const galleryMediaId = this.getGalleryMediaIdForVariant(variantForMedia);
        const suppressGalleryActivation = this.suppressNextOptionGalleryActivation && window.innerWidth < 750;
        this.suppressNextOptionGalleryActivation = false;
        if (galleryMediaId && !suppressGalleryActivation) {
          this.querySelector('media-gallery')?.setActiveMedia?.(galleryMediaId, false);
        }

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const requestUrl = resolvedVariant?.id
          ? this.buildRequestUrlWithVariant(productUrl, resolvedVariant.id, shouldFetchFullPage)
          : this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      initSelectedVariantMediaHandler() {
        this.variantSelectors?.addEventListener('change', () => {
          window.setTimeout(() => this.showSelectedVariantMedia(), 0);
        });
        this.variantSelectors?.addEventListener('pointerup', () => {
          window.setTimeout(() => this.showSelectedVariantMedia(), 0);
        });
      }

	      showSelectedVariantMedia() {
	        this.syncGalleryToVariant(
	          this.findVariantByOptions(this.getCurrentSelectedOptions()) || this.getSelectedVariant(this)
	        );
	      }

	      syncGalleryToVariant(variant) {
	        if (!variant) return;

	        const featuredMediaId = variant?.featured_media?.id;
	        const galleryMediaId = this.getGalleryMediaIdForVariant(variant);
	        if (!featuredMediaId && !galleryMediaId) return;

	        this.querySelector('media-gallery')?.setActiveMedia?.(
	          galleryMediaId || `${this.dataset.section}-${featuredMediaId}`,
	          false
	        );
	      }

	      initThumbnailVariantSync() {
	        this.querySelector('media-gallery')?.addEventListener('thumbnailVariantSync', (event) => {
	          this.syncVariantFromThumbnail(event.detail);
	        });
	      }

	      syncVariantFromThumbnail({ label, galleryRole, variantId, source = 'thumbnail' }) {
	        if (galleryRole === 'artwork-hero') return;

	        const variantFromId = this.findVariantById(variantId);
	        if (variantFromId) {
	          this.setVariantOptionsForVariant(variantFromId, { source });
	          return;
	        }

	        const normalizedLabel = this.normalizeThumbnailLabel(label);

	        const labelParts = normalizedLabel.match(/^(\d+)\s*[x×]\s*(\d+")\s+(.+)$/i);
	        if (!labelParts) return;

	        this.setVariantOptionsFromMedia({
	          size: `${labelParts[1]}X${labelParts[2]}`,
	          frame: labelParts[3],
	        }, { source });
	      }

	      setVariantOptionsFromMedia({ size, frame }, options = {}) {
	        const variantSelectors = this.variantSelectors;
	        if (!variantSelectors) return;
	        const isMobileSwipeSync = options.source === 'swipe' && window.innerWidth < 750;
	        const isThumbnailSync = options.source === 'thumbnail';
	        const selects = Array.from(variantSelectors.querySelectorAll('select'));
	        const changedSelects = [
	          this.setSelectValue(this.getVariantSelect('Size', 0, selects), size),
	          this.setSelectValue(this.getVariantSelect('Frame', 1, selects), frame),
	        ].filter(Boolean);
          const resolvedVariant = this.findVariantByOptions([size, frame]);

	        if (!changedSelects.length) {
            if (resolvedVariant) this.syncLocalVariantState(resolvedVariant);
            return;
          }

          if (isThumbnailSync) {
            if (resolvedVariant) this.syncLocalVariantState(resolvedVariant);
            return;
          }

	        this.suppressNextOptionGalleryActivation = isMobileSwipeSync;
	        this.skipNextMediaActivation = isMobileSwipeSync;
          this.publishVariantSelectionChange(
            variantSelectors,
            changedSelects[changedSelects.length - 1],
            resolvedVariant
          );
	      }

	      setVariantOptionsForVariant(variant, { source = 'thumbnail' } = {}) {
	        const variantSelectors = this.variantSelectors;
	        if (!variantSelectors || !variant) return;
	        const isMobileSwipeSync = source === 'swipe' && window.innerWidth < 750;
	        const isThumbnailSync = source === 'thumbnail';

	        const selects = Array.from(variantSelectors.querySelectorAll('select'));
	        const changedSelects = [
	          this.setSelectValue(this.getVariantSelect('Size', 0, selects), variant.options?.[0]),
	          this.setSelectValue(this.getVariantSelect('Frame', 1, selects), variant.options?.[1]),
	        ].filter(Boolean);

	        const galleryMediaId = this.getGalleryMediaIdForVariant(variant);
	        if (galleryMediaId && !isMobileSwipeSync && !isThumbnailSync) {
	          this.querySelector('media-gallery')?.setActiveMedia?.(galleryMediaId, false);
	        }

        if (!changedSelects.length) {
          this.syncLocalVariantState(variant);
	          return;
	        }

          if (isThumbnailSync) {
            this.syncLocalVariantState(variant);
            return;
          }

	        this.suppressNextOptionGalleryActivation = isMobileSwipeSync;
	        this.skipNextMediaActivation = isMobileSwipeSync;
          this.syncLocalVariantState(variant);
          this.publishVariantSelectionChange(
            variantSelectors,
            changedSelects[changedSelects.length - 1],
            variant
          );
	      }

	      publishVariantSelectionChange(variantSelectors, changedSelect, variant = null) {
	        if (!variantSelectors || !changedSelect) return;

          if (variant?.id) this.syncLocalVariantState(variant);

          changedSelect.dispatchEvent(new Event('change', { bubbles: true }));
	      }

	      findVariantById(variantId) {
	        if (!variantId) return null;
	        return this.productVariants.find((variant) => String(variant.id) === String(variantId));
	      }

	      getVariantSelect(optionName, indexFallback, selects) {
	        return (
	          selects.find((element) => element.name === `options[${optionName}]`) ||
	          selects.find((element) => element.name?.toLowerCase().includes(optionName.toLowerCase())) ||
	          selects[indexFallback] ||
	          null
	        );
	      }

	      setSelectValue(select, value) {
	        if (!select || !value) return null;

	        const normalizedValue = this.normalizeVariantOption(value);
	        const matchingOption = Array.from(select.options).find(
	          (option) => this.normalizeVariantOption(option.value) === normalizedValue
	        );
	        if (!matchingOption || select.value === matchingOption.value) return null;

	        select.value = matchingOption.value;
	        Array.from(select.options).forEach((option) => {
	          const isSelected = option === matchingOption;
	          option.selected = isSelected;
	          if (isSelected) {
	            option.setAttribute('selected', 'selected');
	          } else {
	            option.removeAttribute('selected');
	          }
	        });

	        return select;
	      }

	      findVariantByOptions(selectedOptions) {
	        return this.productVariants.find((variant) =>
	          variant.options?.every(
	            (option, index) =>
	              this.normalizeVariantOption(option) === this.normalizeVariantOption(selectedOptions[index])
	          )
	        );
	      }

	      getCurrentSelectedOptions() {
	        return Array.from(this.variantSelectors?.querySelectorAll('select') || [])
	          .map((select) => select.value)
	          .filter(Boolean);
	      }

	      getGalleryMediaIdForVariant(variant) {
	        const options = variant?.options || [];
	        if (options.length < 2) return null;

	        const label = this.normalizeThumbnailLabel(`${options[0]} ${options[1]}`);
	        const matchingThumbnail = Array.from(
	          this.querySelectorAll('media-gallery [data-target][data-thumbnail-label]')
	        ).find((thumbnail) => this.normalizeThumbnailLabel(thumbnail.dataset.thumbnailLabel) === label);

	        return matchingThumbnail?.dataset.target || null;
	      }

	      normalizeThumbnailLabel(label) {
	        return String(label || '')
	          .split('Thumbnail Label:')
	          .pop()
	          .trim()
	          .replace(/\s+/g, ' ')
	          .replace(/(\d+)\s*[x×]\s*(\d+")/i, '$1X$2')
	          .toLowerCase();
	      }

	      normalizeVariantOption(value) {
	        return String(value || '')
	          .trim()
	          .replace(/\s+/g, ' ')
	          .replace(/(\d+)\s*[x×]\s*(\d+")/i, '$1X$2')
	          .toLowerCase();
	      }

	      get productVariants() {
	        const variantsJson = this.variantSelectors?.querySelector('[data-product-variants]')?.textContent;
	        if (variantsJson) return JSON.parse(variantsJson);

          const productMetaVariants = window.meta?.product?.variants;
          if (!Array.isArray(productMetaVariants)) return [];

          return productMetaVariants.map((variant) => {
            const options = String(variant.public_title || variant.name || '')
              .replace(/^.*? - /, '')
              .split(' / ')
              .map((option) => option.trim())
              .filter(Boolean);

            return {
              ...variant,
              available: variant.available !== false,
              compare_at_price: variant.compare_at_price ?? null,
              options,
              title: variant.public_title || options.join(' / '),
            };
          });
	      }

	      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      buildRequestUrlWithVariant(url, variantId, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (variantId) {
          params.push(`variant=${variantId}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          window.setTimeout(() => {
            const resolvedVariant = this.findVariantByOptions(this.getCurrentSelectedOptions()) || variant;
            this.syncGalleryToVariant(resolvedVariant);
          }, 0);
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      syncLocalVariantState(variant) {
        if (!variant?.id) return;

        this.updateURL(this.dataset.url, variant.id);
        this.updateVariantInputs(variant.id);
        this.updateSelectedVariantJson(variant);
        this.updateDisplayedPrice(variant);
        this.updatePaymentTerms(variant.id);
      }

      updateSelectedVariantJson(variant) {
        const selectedVariantScript = this.variantSelectors?.querySelector('[data-selected-variant]');
        if (!selectedVariantScript) return;
        selectedVariantScript.textContent = JSON.stringify(variant);
      }

      updateDisplayedPrice(variant) {
        const priceContainer = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceContainer || variant?.price == null) return;

        const priceRoot = priceContainer.querySelector('.price');
        const regularPriceNodes = priceContainer.querySelectorAll('.price__regular .price-item--regular');
        const comparePriceNodes = priceContainer.querySelectorAll('.price__sale .price-item--regular, .variant-item__old-price');
        const salePriceNodes = priceContainer.querySelectorAll('.price__sale .price-item--sale');

        const formattedPrice = this.formatMoneyWithCurrency(variant.price);
        const hasCompareAtPrice = Number(variant.compare_at_price) > Number(variant.price);
        const formattedCompareAtPrice = hasCompareAtPrice
          ? this.formatMoneyWithCurrency(variant.compare_at_price)
          : '';

        regularPriceNodes.forEach((node) => {
          node.textContent = formattedPrice;
        });

        salePriceNodes.forEach((node) => {
          node.textContent = formattedPrice;
        });

        comparePriceNodes.forEach((node) => {
          node.textContent = formattedCompareAtPrice;
        });

        if (priceRoot) {
          priceRoot.classList.toggle('price--sold-out', variant.available === false);
          priceRoot.classList.toggle('price--on-sale', hasCompareAtPrice);
        }
      }

      updatePaymentTerms(variantId) {
        this.querySelector('shopify-payment-terms')?.setAttribute('variant-id', variantId);
      }

      formatMoneyWithCurrency(valueInCents) {
        const amount = Number(valueInCents || 0) / 100;
        return `${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(amount)} USD`;
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const variant = this.getSelectedVariant(html) || this.getSelectedVariant(this);
        const galleryMediaId = this.getGalleryMediaIdForVariant(variant);
        if (!variantFeaturedMediaId && !galleryMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        const preserveMobileGalleryTrack = window.innerWidth < 750;

        if (mediaGallerySource && mediaGalleryDestination && !preserveMobileGalleryTrack) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // Prefer the curated gallery item for this variant. This lets transparent
        // display media override older Artelo-bound media without breaking variants.
	        const shouldSkipActivation = this.skipNextMediaActivation && window.innerWidth < 750;
	        this.skipNextMediaActivation = false;

	        if (!shouldSkipActivation) {
	          this.querySelector(`media-gallery`)?.setActiveMedia?.(
	            galleryMediaId || `${this.dataset.section}-${variantFeaturedMediaId}`,
	            false
	          );
	        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
